import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Segment } from '../models/Segment';
import { Paragraph } from '../models/Paragraph';
import { TextElement } from '../models/TranscriptElement';

/**
 * State information for undo operations
 */
interface ElementSpeakerState {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  segmentSpeakerLabel: string;
  elementSpeakerLabel: string;
  // For undo operations when a paragraph was split or moved
  operationType: 'no_change' | 'paragraph_split' | 'paragraph_moved';
  removedParagraph?: Paragraph;
  originalParagraphElements?: any[];
  newSegmentId?: string;
  newParagraphId?: string;
}

/**
 * Command to assign a speaker to a specific element
 * 
 * This may involve complex operations like paragraph splitting and segment creation
 * when the element's new speaker is different from its current segment's speaker.
 */
export class AssignElementSpeakerCommand extends AbstractTranscriptEditorCommand {
  private segmentId: string;
  private paragraphId: string;
  private elementId: string;
  private speakerLabel: string;
  private datoSpeakerId: string;
  private userId: string;
  
  // State for undo operations
  private state: ElementSpeakerState | null = null;
  
  /**
   * Create a new AssignElementSpeakerCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to assign speaker to
   * @param speakerLabel Label of the speaker to assign
   * @param datoSpeakerId Dato CMS ID of the speaker
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    speakerLabel: string,
    datoSpeakerId: string,
    userId: string
  ) {
    super(`Assign speaker ${speakerLabel} to element ${elementId.slice(0, 6)}...`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.speakerLabel = speakerLabel;
    this.datoSpeakerId = datoSpeakerId;
    this.userId = userId;
    
    // Set this command to require sorting after execution
    this.setRequiresSorting(true);
    
    // Add metadata for debugging
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('elementId', elementId);
    this.setMetadata('speakerLabel', speakerLabel);
    this.setMetadata('datoSpeakerId', datoSpeakerId);
    this.setMetadata('userId', userId);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      this.editor.beginBatch();
      
      // Validate input parameters
      const container = this.editor.getContainer();
      if (!container) {
        console.error('Container not available');
        this.editor.endBatch();
        return false;
      }
      
      // Get the segment and paragraph
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        console.error(`Segment ${this.segmentId} not found`);
        this.editor.endBatch();
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        console.error(`Paragraph ${this.paragraphId} not found`);
        this.editor.endBatch();
        return false;
      }
      
      // Get the element
      const elementIndex = paragraph.elements.findIndex(e => e.id === this.elementId);
      if (elementIndex === -1) {
        console.error(`Element ${this.elementId} not found`);
        this.editor.endBatch();
        return false;
      }
      
      // Store initial state for undo
      this.captureState(segment, paragraph, elementIndex);
      
      // Check if we need to create a new segment
      const needsNewSegment = segment.speakerLabel !== this.speakerLabel;
      
      if (needsNewSegment) {
        // We need to split the paragraph at this element and assign the new segment
        const success = this.handleSegmentCreation(segment, paragraph, elementIndex);
        this.editor.endBatch();
        return success;
      } else {
        // The element is already in a segment with the correct speaker
        // This is a simple update - just update the speaker label on the element
        const element = paragraph.elements[elementIndex];
        if (element.type === 'text') {
          (element as TextElement).speakerLabel = this.speakerLabel;
        }
        
        // Update speaker mapping if dato ID is provided
        if (this.datoSpeakerId) {
          this.editor.updateSpeakerMapping(this.speakerLabel, this.datoSpeakerId);
        }
        
        // Notify observers
        this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
          segmentId: this.segmentId,
          speakerLabel: this.speakerLabel
        });
        
        // Record the operation
        this.editor.recordOperation({
          type: 'speaker_assignment',
          segmentId: this.segmentId,
          data: {
            elementId: this.elementId,
            oldSpeakerLabel: this.state?.elementSpeakerLabel || '',
            newSpeakerLabel: this.speakerLabel,
            datoSpeakerId: this.datoSpeakerId,
            scope: 'element'
          },
          userId: this.userId,
          timestamp: Date.now()
        });
        
        this.editor.endBatch();
        return true;
      }
    } catch (error) {
      console.error('Error executing AssignElementSpeakerCommand:', error);
      this.editor.endBatch();
      return false;
    }
  }
  
  /**
   * Handle the case where a new segment needs to be created
   * 
   * @param segment The original segment
   * @param paragraph The paragraph containing the element
   * @param elementIndex The index of the element in the paragraph
   * @returns True if successful
   */
  private handleSegmentCreation(
    segment: Segment,
    paragraph: Paragraph,
    elementIndex: number
  ): boolean {
    // Get all elements from this point forward
    const elementsToMove = paragraph.elements.slice(elementIndex);
    if (elementsToMove.length === 0) {
      console.error('No elements to move');
      return false;
    }
    
    // Create a new paragraph ID
    const newParagraphId = `paragraph_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    // Create a new segment ID
    const newSegmentId = `segment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    // Update our state with the new IDs for undo
    if (this.state) {
      this.state.newSegmentId = newSegmentId;
      this.state.newParagraphId = newParagraphId;
    }
    
    // Determine timing for the new segment
    const textElements = elementsToMove.filter(e => e.type === 'text') as TextElement[];
    if (textElements.length === 0) {
      console.error('No text elements to move');
      return false;
    }
    
    const startTime = textElements[0].startTime || 0;
    const endTime = textElements[textElements.length - 1].endTime || startTime;
    
    // Check if the paragraph will be empty after splitting
    if (elementIndex === 0) {
      // The entire paragraph is moving to the new segment
      
      // Remove paragraph from current segment
      const paragraphIndex = segment.paragraphs.findIndex(p => p.id === this.paragraphId);
      if (paragraphIndex === -1) {
        console.error(`Paragraph ${this.paragraphId} not found in segment`);
        return false;
      }
      
      const removedParagraph = segment.paragraphs.splice(paragraphIndex, 1)[0];
      
      // Update state for undo
      if (this.state) {
        this.state.operationType = 'paragraph_moved';
        this.state.removedParagraph = removedParagraph;
      }
      
      // Update speaker label for all elements
      for (const element of removedParagraph.elements) {
        if (element.type === 'text') {
          (element as TextElement).speakerLabel = this.speakerLabel;
        }
      }
      
      // Update the paragraph's segment ID
      removedParagraph.segmentId = newSegmentId;
      
      // Create the new segment
      const newSegment = new Segment(
        newSegmentId,
        this.speakerLabel,
        startTime,
        endTime,
        [removedParagraph]
      );
      
      // Add to container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      container.addSegment(newSegment);
    } else {
      // We need to split the paragraph
      
      // Store original elements for undo
      if (this.state) {
        this.state.operationType = 'paragraph_split';
        this.state.originalParagraphElements = [...paragraph.elements];
      }
      
      // Create a new paragraph with the elements to move
      const newParagraph = new Paragraph(
        newParagraphId,
        startTime,
        endTime,
        elementsToMove,
        newSegmentId
      );
      
      // Update speaker label for all moved elements
      for (const element of newParagraph.elements) {
        if (element.type === 'text') {
          (element as TextElement).speakerLabel = this.speakerLabel;
        }
        // Update segment and paragraph IDs
        element.segmentId = newSegmentId;
        element.paragraphId = newParagraphId;
      }
      
      // Remove these elements from the original paragraph
      paragraph.elements = paragraph.elements.slice(0, elementIndex);
      
      // Update paragraph timing
      const remainingTextElements = paragraph.elements
        .filter(e => e.type === 'text') as TextElement[];
        
      if (remainingTextElements.length > 0) {
        paragraph.startTime = remainingTextElements[0].startTime || 0;
        paragraph.endTime = remainingTextElements[remainingTextElements.length - 1].endTime || 
                          paragraph.startTime;
      }
      
      // Create the new segment
      const newSegment = new Segment(
        newSegmentId,
        this.speakerLabel,
        startTime,
        endTime,
        [newParagraph]
      );
      
      // Add to container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      container.addSegment(newSegment);
    }
    
    // Update speaker mapping if dato ID is provided
    if (this.datoSpeakerId) {
      this.editor.updateSpeakerMapping(this.speakerLabel, this.datoSpeakerId);
    }
    
    // Record the operation
    this.editor.recordOperation({
      type: 'segment_create',
      segmentId: newSegmentId,
      data: {
        speakerLabel: this.speakerLabel,
        datoSpeakerId: this.datoSpeakerId,
        originalSegmentId: this.segmentId,
        originalParagraphId: this.paragraphId,
        startElementId: this.elementId
      },
      userId: this.userId,
      timestamp: Date.now()
    });
    
    // Notify observers
    this.editor.notifyObservers(
      TranscriptEditorChangeType.SEGMENT_CREATED, 
      {
        segmentId: newSegmentId,
        speakerLabel: this.speakerLabel
      }
    );
    
    return true;
  }
  
  /**
   * Capture the current state for undo operations
   * 
   * @param segment The segment containing the element
   * @param paragraph The paragraph containing the element
   * @param elementIndex The index of the element in the paragraph
   */
  private captureState(
    segment: Segment,
    paragraph: Paragraph,
    elementIndex: number
  ): void {
    const element = paragraph.elements[elementIndex];
    this.state = {
      segmentId: this.segmentId,
      paragraphId: this.paragraphId,
      elementId: this.elementId,
      segmentSpeakerLabel: segment.speakerLabel,
      elementSpeakerLabel: element.type === 'text' ? (element as TextElement).speakerLabel || '' : '',
      operationType: 'no_change'
    };
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    try {
      this.editor.beginBatch();
      
      if (!this.state) {
        console.error('Cannot undo AssignElementSpeakerCommand: no state available');
        this.editor.endBatch();
        return false;
      }
      
      const container = this.editor.getContainer();
      if (!container) {
        console.error('Container not available for undo');
        this.editor.endBatch();
        return false;
      }
      
      let success = false;
      
      switch (this.state.operationType) {
        case 'no_change':
          // Simple case: just restore the original speaker label
          success = this.undoSimpleAssignment();
          break;
          
        case 'paragraph_moved':
          // Undo a paragraph that was moved to a new segment
          success = this.undoParagraphMove();
          break;
          
        case 'paragraph_split':
          // Undo a paragraph that was split
          success = this.undoParagraphSplit();
          break;
          
        default:
          console.error(`Unknown operation type: ${this.state.operationType}`);
          success = false;
      }
      
      this.editor.endBatch();
      return success;
    } catch (error) {
      console.error('Error undoing AssignElementSpeakerCommand:', error);
      this.editor.endBatch();
      return false;
    }
  }
  
  /**
   * Undo a simple speaker assignment that didn't require structural changes
   * 
   * @returns True if successful
   */
  private undoSimpleAssignment(): boolean {
    if (!this.state) return false;
    
    const container = this.editor.getContainer();
    if (!container) return false;
    
    const segment = container.getSegment(this.state.segmentId);
    if (!segment) return false;
    
    const paragraph = segment.getParagraph(this.state.paragraphId);
    if (!paragraph) return false;
    
    const element = paragraph.getElement(this.state.elementId);
    if (!element || element.type !== 'text') return false;
    
    // Restore the original speaker label
    (element as TextElement).speakerLabel = this.state.elementSpeakerLabel;
    
    // Notify observers
    this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
      segmentId: this.state.segmentId,
      elementId: this.state.elementId,
      speakerLabel: this.state.elementSpeakerLabel
    });
    
    return true;
  }
  
  /**
   * Undo a paragraph move operation
   * 
   * @returns True if successful
   */
  private undoParagraphMove(): boolean {
    if (!this.state || !this.state.newSegmentId || !this.state.removedParagraph) return false;
    
    const container = this.editor.getContainer();
    if (!container) return false;
    
    // Get the new segment that was created
    const newSegment = container.getSegment(this.state.newSegmentId);
    if (!newSegment) return false;
    
    // Get the original segment
    const originalSegment = container.getSegment(this.state.segmentId);
    if (!originalSegment) return false;
    
    // Find the paragraph in the new segment
    if (!this.state || !this.state.paragraphId) return false;
    const paragraphIndex = newSegment.paragraphs.findIndex(p => p.id === this.state?.paragraphId!);
    if (paragraphIndex === -1) return false;
    
    // Remove the paragraph from the new segment
    const paragraph = newSegment.paragraphs.splice(paragraphIndex, 1)[0];
    
    // Restore original speaker labels
    for (const element of paragraph.elements) {
      if (element.type === 'text') {
        (element as TextElement).speakerLabel = this.state.segmentSpeakerLabel;
      }
      // Update segment ID
      element.segmentId = this.state.segmentId;
    }
    
    // Update paragraph's segment ID
    paragraph.segmentId = this.state.segmentId;
    
    // Add paragraph back to the original segment
    originalSegment.addParagraph(paragraph);
    
    // Remove the new segment if it's now empty
    if (newSegment.paragraphs.length === 0) {
      container.removeSegment(this.state.newSegmentId);
      
      // Notify observers about segment deletion
      this.editor.notifyObservers(TranscriptEditorChangeType.SEGMENT_DELETED, {
        segmentId: this.state.newSegmentId
      });
    }
    
    // Notify observers
    this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
      container: container
    });
    
    return true;
  }
  
  /**
   * Undo a paragraph split operation
   * 
   * @returns True if successful
   */
  private undoParagraphSplit(): boolean {
    if (!this.state || !this.state.newSegmentId || !this.state.newParagraphId || !this.state.originalParagraphElements) {
      return false;
    }
    
    const container = this.editor.getContainer();
    if (!container) return false;
    
    // Get the original segment
    const originalSegment = container.getSegment(this.state.segmentId);
    if (!originalSegment) return false;
    
    // Get the new segment
    const newSegment = container.getSegment(this.state.newSegmentId);
    if (!newSegment) return false;
    
    // Get the original paragraph
    const originalParagraph = originalSegment.getParagraph(this.state.paragraphId);
    if (!originalParagraph) return false;
    
    // Get the new paragraph
    if (!this.state || !this.state.newParagraphId) return false;
    const newParagraphIndex = newSegment.paragraphs.findIndex(p => p.id === this.state?.newParagraphId!);
    if (newParagraphIndex === -1) return false;
    
    // Remove the new paragraph from the new segment
    newSegment.paragraphs.splice(newParagraphIndex, 1);
    
    // Restore the original paragraph elements
    originalParagraph.elements = [...this.state.originalParagraphElements];
    
    // Restore original speaker labels
    for (const element of originalParagraph.elements) {
      if (element.type === 'text' && element.id === this.state.elementId) {
        (element as TextElement).speakerLabel = this.state.elementSpeakerLabel;
      }
      
      // Update segment and paragraph IDs
      element.segmentId = this.state.segmentId;
      element.paragraphId = this.state.paragraphId;
    }
    
    // Restore paragraph timing
    const textElements = originalParagraph.elements
      .filter(e => e.type === 'text') as TextElement[];
      
    if (textElements.length > 0) {
      originalParagraph.startTime = textElements[0].startTime || 0;
      originalParagraph.endTime = textElements[textElements.length - 1].endTime || originalParagraph.startTime;
    }
    
    // Remove the new segment if it's now empty
    if (newSegment.paragraphs.length === 0) {
      container.removeSegment(this.state.newSegmentId);
      
      // Notify observers about segment deletion
      this.editor.notifyObservers(TranscriptEditorChangeType.SEGMENT_DELETED, {
        segmentId: this.state.newSegmentId
      });
    }
    
    // Notify observers
    this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
      container: container
    });
    
    return true;
  }
}